import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button } from '../common';

/**
 * ImportCSVModal - Import users from CSV file
 */
const ImportCSVModal = ({ onClose, onSuccess }) => {
  const [csvText, setCsvText] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvText(event.target.result);
        parseCSV(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const parseCSV = (text) => {
    setError('');
    setPreview([]);
    setShowPreview(false);
    
    if (!text.trim()) {
      setError('CSV file is empty');
      return;
    }

    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      setError('CSV must have at least a header row and one data row');
      return;
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredFields = ['firstname', 'lastname', 'email', 'password'];
    
    const missingFields = requiredFields.filter(field => !header.includes(field));
    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    const firstNameIdx = header.indexOf('firstname');
    const lastNameIdx = header.indexOf('lastname');
    const emailIdx = header.indexOf('email');
    const passwordIdx = header.indexOf('password');
    const isAdminIdx = header.indexOf('isadmin');

    const parsedUsers = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      
      if (values.length < requiredFields.length) {
        setError(`Row ${i + 1}: Not enough columns`);
        return;
      }

      const user = {
        firstName: values[firstNameIdx],
        lastName: values[lastNameIdx],
        email: values[emailIdx],
        password: values[passwordIdx],
        isAdmin: isAdminIdx >= 0 ? (values[isAdminIdx]?.toLowerCase() === 'true' || values[isAdminIdx] === '1') : false
      };

      if (!user.email || !user.email.includes('@')) {
        setError(`Row ${i + 1}: Invalid email address`);
        return;
      }

      if (!user.firstName || !user.lastName || !user.password) {
        setError(`Row ${i + 1}: Missing required field(s)`);
        return;
      }

      parsedUsers.push(user);
    }

    if (parsedUsers.length === 0) {
      setError('No valid users found in CSV');
      return;
    }

    setPreview(parsedUsers);
    setShowPreview(true);
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    
    try {
      const result = await api.post('/users/import', { users: preview });
      const skippedMsg = result.skipped > 0 ? ` Skipped: ${result.skipped} (already exist or invalid).` : '';
      setImportSuccess(`Import complete! Imported ${result.imported} user${result.imported !== 1 ? 's' : ''}.${skippedMsg}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'firstName,lastName,email,password,isAdmin\nJohn,Doe,john.doe@example.com,password123,false\nJane,Smith,jane.smith@example.com,pass456,true';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Import Users from CSV</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            <strong>CSV Format:</strong> firstName, lastName, email, password, isAdmin (optional)
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Button variant="secondary" size="small" onClick={downloadTemplate}>
              Download Template CSV
            </Button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {importSuccess && <div className="alert alert-success">{importSuccess}</div>}

          <div className="form-group">
            <label className="form-label">Upload CSV File</label>
            <input type="file" accept=".csv" onChange={handleFileUpload} className="form-input" />
          </div>

          <div className="form-group">
            <label className="form-label">Or paste CSV data</label>
            <textarea
              className="form-input form-textarea"
              value={csvText}
              onChange={(e) => { setCsvText(e.target.value); parseCSV(e.target.value); }}
              placeholder="firstName,lastName,email,password,isAdmin&#10;John,Doe,john.doe@example.com,password123,false&#10;Jane,Smith,jane.smith@example.com,pass456,true"
              style={{ minHeight: '120px', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </div>

          {showPreview && preview.length > 0 && (
            <div className="form-group">
              <label className="form-label">Preview ({preview.length} users)</label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: '6px', padding: '0.5rem', background: 'var(--color-bg)' }}>
                {preview.map((user, idx) => (
                  <div key={idx} className="list-item" style={{ marginBottom: '0.35rem', padding: '0.5rem' }}>
                    <div className="list-item-content">
                      <div className="list-item-title" style={{ fontSize: '0.85rem' }}>
                        {user.firstName} {user.lastName}
                        {user.isAdmin && <span className="badge badge-admin" style={{ marginLeft: '0.5rem' }}>Admin</span>}
                      </div>
                      <div className="list-item-meta">{user.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={importSuccess ? onSuccess : onClose}>
            {importSuccess ? 'Done' : 'Cancel'}
          </Button>
          {!importSuccess && showPreview && preview.length > 0 && (
            <Button variant="primary" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing...' : `Import ${preview.length} User${preview.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

ImportCSVModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ImportCSVModal;
